import { TextField } from '@mui/material'
import React, { useState } from 'react'
import { NumberInputProps, useInput, useTranslate } from 'react-admin'

/**
 * Custom parse function to intercept the value from the input and set it to undefined
 * in the case it is empty string, or turn it into a number (from its string number value "8" -> 8) in the case it is not empty
 *
 * @param v the value stored in the input's inner state
 * @returns the value to commit to the form state
 */
const parse = (v?: string | number) => {
  return v === '' || v === undefined ? undefined : Number(v)
}

const KNumberInput: React.FC<NumberInputProps> = ({
  source,
  validate,
  helperText,
  label,
  ...rest
}) => {
  const translate = useTranslate()

  /**
   * This onChange is executed before the native onChange f the input.
   * We use it to keep the local state where the input reads its value
   */
  const onChange: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = ({ target: { value } }) => {
    if (value === '') {
      setValue(value)
    } else {
      setValue(Number(value))
    }
  }

  // https://marmelab.com/react-admin/Inputs.html#the-useinput-hook
  const {
    field: { value: fieldVal, ...fieldRest },
    fieldState: { isTouched, error },
    formState: { isSubmitted },
    isRequired,
  } = useInput({ source, parse, onChange, validate, ...rest })

  /**
   * Local value of our input component, it's needed so that the wrapped input is always reading an empty string as empty value
   * so that it does not automatically set the value in the form to empty string
   */
  const [value, setValue] = useState<number | string>(fieldVal)

  return (
    <TextField
      type="number"
      value={value}
      error={(isTouched || isSubmitted) && !!error}
      helperText={
        (isTouched || isSubmitted) && !!error ? error.message : helperText
      }
      required={isRequired}
      label={typeof label === 'string' ? translate(label) : label}
      {...fieldRest}
    />
  )
}

export default KNumberInput
